// Globals
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

// Components
import { FormItem, IFormInstance, showErrorToast } from '@/components';
import { UploadIcon } from '@/icons';
import {
  StyledFormItemWrapper,
  StyledHeaderBlock,
  StyledTitle,
  StyledUploadButton,
  StyledPhotoList,
  StyledPhotoWrapper,
  StyledUploadControl,
  StyledUploadIconWrapper,
  StyledUploadDraggerTitle,
  StyledUploadDraggerText,
} from './ProductPhotosFieldStyles';
import ProductPhotoItem from './ProductPhotoItem';
import PureImageUploader from 'src/shared/components/PureImageUploader/PureImageUploader';

// Helpers
import { MAX_IMAGE_SIZE, IMAGE_MIME_TYPES } from '@/constants/files';
import { ProductMainInfoFormFieldsNames, PhotoItem, fileToPhotoItem } from '../../helpers';
import { useDragAndDrop } from '../../hooks';

interface IProductPhotosField {
  formInstance: IFormInstance;
  initialPhotos?: PhotoItem[];
  handleValuesChange?: () => void;
}

const ProductPhotosField = ({ formInstance, initialPhotos, handleValuesChange }: IProductPhotosField) => {
  const { t } = useTranslation(['products']);
  const [photoItems, setPhotoItems] = useState<PhotoItem[]>([]);
  const updatePhotoItemsWithOrder = (newItems: PhotoItem[]) => {
    setPhotoItems(newItems.map((item, index) => ({ ...item, order: index + 1 })));
    handleValuesChange?.();
  };
  const refs = useDragAndDrop(photoItems, updatePhotoItemsWithOrder);

  const handleUpload = (file: File) => {
    setPhotoItems((prevItems) => {
      if (prevItems.length >= 10) {
        showErrorToast({ message: t('product_photos_limit_error') });
        return prevItems;
      }

      const newItems = [...prevItems, fileToPhotoItem(file, prevItems.length + 1, uuidv4())];
      return newItems.slice(0, 10);
    });
    handleValuesChange?.();
    return false;
  };

  const handleDelete = (itemToDelete: PhotoItem) => {
    setPhotoItems((prevItems) =>
      prevItems.filter((item) => item.uid !== itemToDelete.uid).map((item, index) => ({ ...item, order: index + 1 })),
    );
    handleValuesChange?.();
  };

  useEffect(() => {
    if (initialPhotos) {
      setPhotoItems(initialPhotos);
    }
  }, [initialPhotos]);

  useEffect(() => {
    const formData = photoItems.map((item) => ({
      uid: item.uid,
      name: item.name,
      order: item.order,
      status: 'done' as const,
      ...(item.isServerPhoto ? { url: item.originalUrl } : { originFileObj: item.originFileObj }),
    }));
    formInstance.setFieldsValue({
      photos: formData,
    });
  }, [photoItems]);

  return (
    <StyledFormItemWrapper>
      <StyledHeaderBlock>
        <StyledTitle>{t('main_info_product_photos_label')}</StyledTitle>
        <StyledUploadControl
          disabled={photoItems.length >= 10}
          imageMaxSize={MAX_IMAGE_SIZE}
          accept={IMAGE_MIME_TYPES}
          maxCount={10}
          onChange={(file) => handleUpload(file as File)}
          showUploadList={false}
        >
          <StyledUploadButton>{t('main_info_product_upload_photos_button')}</StyledUploadButton>
        </StyledUploadControl>
      </StyledHeaderBlock>
      <FormItem name={ProductMainInfoFormFieldsNames.PHOTOS}>
        {photoItems.length === 0 ? (
          <PureImageUploader
            imageMaxSize={MAX_IMAGE_SIZE}
            accept={IMAGE_MIME_TYPES}
            maxCount={10}
            onChange={(file) => handleUpload(file as File)}
            showUploadList={false}
          >
            <StyledUploadIconWrapper>
              <UploadIcon />
            </StyledUploadIconWrapper>
            <StyledUploadDraggerTitle>{t('main_info_product_upload_photos_title')}</StyledUploadDraggerTitle>
            <StyledUploadDraggerText>{t('main_info_product_upload_photos_subtitle')}</StyledUploadDraggerText>
            <StyledUploadDraggerText>
              {t('main_info_product_upload_photos_requirements', {
                formats: 'JPEG, JPG, PNG, WEBP',
                size: '10 MB',
                limit: 10,
              })}
            </StyledUploadDraggerText>
          </PureImageUploader>
        ) : (
          <StyledPhotoList>
            {photoItems.map((item, index) => (
              <StyledPhotoWrapper key={`${item.name}-${index}`} ref={(el) => (refs.current[index] = el)}>
                <ProductPhotoItem
                  item={item}
                  index={index}
                  onDelete={handleDelete}
                  isServerPhoto={item.isServerPhoto}
                />
              </StyledPhotoWrapper>
            ))}
          </StyledPhotoList>
        )}
      </FormItem>
    </StyledFormItemWrapper>
  );
};

export default ProductPhotosField;
